import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star, Quote, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";

import SEO, { orgSchema, faqSchema } from "@/components/SEO";
import { Reveal, MaskLine, lineParent, stagger, item } from "@/components/motion";
import { SectionHeading, FinalCTA, Eyebrow } from "@/components/sections";
import Marquee from "@/components/Marquee";
import CBTCycle from "@/components/CBTCycle";
import Icon from "@/components/Icon";
import { useAppointment } from "@/components/AppointmentContext";
import { SITE } from "@/lib/site";
import {
  WHY_CHOOSE, WE_HELP, CONDITIONS, HOW_CBT, PROCESS, TESTIMONIALS, INSURANCE, FAQS,
} from "@/data/content";
import { FOUNDER } from "@/data/founder";

export default function Home() {
  const { openDialog } = useAppointment();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <>
      <SEO
        title="Evidence-Based CBT Therapist in Connecticut"
        description="Beacon Counseling is Connecticut's premier Cognitive Behavioral Therapy (CBT) practice — treating anxiety, depression, OCD, ADHD, trauma & more with practical, measurable, results-focused care. Telehealth & in-person in Southington, CT."
        path="/"
        jsonLd={[orgSchema, faqSchema(FAQS)]}
      />

      {/* ============================ HERO ============================ */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden bg-navy">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1594756154841-ac5d160dbf46?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="A calm, confident person enjoying natural light"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/30" />
        </motion.div>
        <div className="pointer-events-none absolute inset-0 grain opacity-[0.12]" />

        <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-6 pt-28 pb-16 lg:px-12">
          <motion.div initial="hidden" animate="show" variants={lineParent} className="max-w-4xl">
            <motion.div variants={item} className="mb-7">
              <span className="inline-flex items-center gap-2 rounded-full glass-navy px-4 py-2 text-sm text-white/80">
                <span className="h-2 w-2 rounded-full bg-teal animate-floaty" />
                Specialized Pediatric Psychotherapy · Connecticut
              </span>
            </motion.div>

            <h1 className="font-display text-[11vw] leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-[4.3rem]">
              <MaskLine>Specialized Pediatric</MaskLine>
              <MaskLine><span className="italic text-teal-light">Psychotherapy</span></MaskLine>
              <MaskLine>for Children, Teens &amp; Families</MaskLine>
            </h1>

            <motion.p variants={item} className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed lg:text-xl">
              Helping children and adolescents overcome anxiety, ADHD, behavioral challenges, OCD,
              depression, emotional regulation difficulties, school-related concerns, and more through
              evidence-based Cognitive Behavioral Therapy (CBT). <span className="text-white font-medium">Now accepting new clients.</span>
            </motion.p>

            <motion.div variants={item} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button onClick={openDialog} data-testid="hero-schedule-btn"
                className="group rounded-full bg-teal hover:bg-teal-dark text-white h-14 px-8 text-base font-medium shadow-premium hover:-translate-y-1 transition-transform">
                Schedule a Consultation
                <ArrowUpRight className="ml-1 h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
              <Link to="/pediatric-psychotherapy" data-testid="hero-learn-btn" className="sm:shrink-0">
                <Button variant="outline"
                  className="w-full sm:w-auto rounded-full border-2 border-white/40 bg-white/15 text-white hover:bg-white/25 hover:text-white h-14 px-8 text-base font-medium backdrop-blur">
                  Learn About Pediatric Therapy
                </Button>
              </Link>
            </motion.div>

            <motion.div variants={item} className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm sm:text-base font-medium text-white/85">
              <span className="flex items-center gap-2"><Check className="h-5 w-5 text-teal-light" /> Telehealth &amp; In-Person</span>
              <span className="flex items-center gap-2"><Check className="h-5 w-5 text-teal-light" /> Most Major Insurance</span>
              <span className="flex items-center gap-2"><Check className="h-5 w-5 text-teal-light" /> Serving All of Connecticut</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============================ MARQUEE ============================ */}
      <section className="border-y border-navy-100 bg-secondary py-8">
        <Marquee items={["Evidence-Based", "Practical", "Goal-Oriented", "Collaborative", "Results-Focused", "Compassionate"]} />
      </section>

      {/* ============================ PEDIATRIC SPECIALTY SPOTLIGHT ============================ */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div className="relative">
                <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-softblue to-transparent blur-2xl opacity-70" />
                <img src="https://images.unsplash.com/photo-1476703993599-0035a21b17a9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="A parent supporting their children at home"
                  className="relative aspect-[4/3] w-full rounded-[2rem] object-cover shadow-premium" />
                <div className="absolute -bottom-6 left-4 sm:left-6 rounded-2xl glass shadow-premium px-6 py-4">
                  <div className="font-display text-2xl text-navy">Now accepting</div>
                  <div className="text-xs text-muted-foreground max-w-[10rem]">new pediatric clients across Connecticut</div>
                </div>
              </div>
            </Reveal>
            <div>
              <SectionHeading eyebrow="Our Primary Specialty"
                title="How therapy helps children & teens."
                intro="Beacon Counseling specializes in pediatric psychotherapy — warm, engaging, evidence-based CBT that helps children build practical coping skills for anxiety, ADHD, big emotions, and school-related stress." />
              <div className="mt-8 space-y-3">
                {["Play-based, age-appropriate CBT that kids actually enjoy", "Parents included as active partners in every step", "Collaboration with schools, teachers & pediatricians", "Personalised care for ages 5 through 17"].map((t, i) => (
                  <Reveal key={t} delay={i * 0.05}>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-teal/10 text-teal"><Check className="h-3.5 w-3.5" /></span>
                      <span className="text-navy/85">{t}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.25}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/pediatric-psychotherapy">
                    <Button className="rounded-full bg-teal hover:bg-teal-dark text-white h-13 px-7 hover:-translate-y-0.5 transition-transform" data-testid="home-pediatric-btn">
                      Explore Pediatric Psychotherapy <ArrowUpRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                  <Button variant="outline" onClick={openDialog} className="rounded-full border-navy/20 h-13 px-7 hover:bg-secondary">
                    Schedule a Consultation
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ TRUSTED BY FAMILIES ============================ */}
      <section className="bg-navy py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal><span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-teal-light">Trusted by Families Across Connecticut</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white text-balance">Personalised, evidence-based care your family can trust.</h2></Reveal>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-6 sm:grid-cols-3">
            {[
              { icon: "Brain", t: "Evidence-Based CBT", d: "Proven, practical methods — not just conversation." },
              { icon: "Users", t: "Parent Collaboration", d: "You're an active partner in your child's progress." },
              { icon: "HeartHandshake", t: "Personalised Care", d: "Every plan is tailored to your child's needs & goals." },
            ].map((c) => (
              <motion.div key={c.t} variants={item} className="rounded-3xl glass-navy p-8 text-center">
                <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-teal/15 text-teal-light"><Icon name={c.icon} className="h-7 w-7" /></span>
                <h3 className="mt-5 font-display text-xl text-white">{c.t}</h3>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </motion.div>
          <Reveal delay={0.2}>
            <div className="mt-12 text-center">
              <Button onClick={openDialog} className="rounded-full bg-teal hover:bg-teal-dark text-white h-13 px-8 hover:-translate-y-0.5 transition-transform" data-testid="trusted-cta">
                Schedule a Consultation
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================ WHY CHOOSE (BENTO) ============================ */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading
            eyebrow="Why Beacon"
            title="Modern mental healthcare, built around you."
            intro="Everything about Beacon Counseling is designed to make world-class, evidence-based care feel personal, practical, and genuinely effective."
          />
          <motion.div
            variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {WHY_CHOOSE.map((c, i) => (
              <motion.div key={c.title} variants={item}
                className="group relative overflow-hidden rounded-3xl border border-navy-100 bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-premium"
                data-testid={`why-card-${i}`}
              >
                <span className="grid h-13 w-13 place-items-center rounded-2xl p-3 bg-softblue/60 text-teal transition-colors group-hover:bg-teal group-hover:text-white">
                  <Icon name={c.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-xl text-navy">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============================ WHAT MAKES CBT DIFFERENT ============================ */}
      <section className="bg-secondary py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div className="relative">
                <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-softblue to-transparent blur-2xl opacity-70" />
                <img
                  src="https://images.unsplash.com/photo-1512438248247-f0f2a5a8b7f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="A calm moment of clarity and perspective"
                  className="relative w-full rounded-[2rem] object-cover shadow-premium aspect-[4/5] sm:aspect-[5/4]"
                />
                <div className="absolute -bottom-6 -right-2 sm:right-6 rounded-2xl glass shadow-premium px-6 py-5">
                  <div className="font-display text-4xl text-navy">#1</div>
                  <div className="text-xs text-muted-foreground max-w-[9rem]">Most-researched form of psychotherapy in the world</div>
                </div>
              </div>
            </Reveal>
            <div>
              <SectionHeading
                eyebrow="What Makes CBT Different"
                title="Therapy that gives you tools — not just conversations."
                intro="Cognitive Behavioral Therapy is one of the world's most researched and effective forms of psychotherapy. Instead of spending years analysing problems, CBT teaches practical strategies you can use immediately to change thinking patterns, behaviours, and emotional responses."
              />
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  { t: "Structured & Focused", d: "Every session has purpose and direction." },
                  { t: "Skills You Keep", d: "Practical tools that last a lifetime." },
                  { t: "Measurable Progress", d: "Track improvement with real data." },
                  { t: "Shorter-Term", d: "Meaningful change, often in 12–20 sessions." },
                ].map((b, i) => (
                  <Reveal key={b.t} delay={i * 0.06}>
                    <div className="rounded-2xl border border-navy-100 bg-white p-5">
                      <div className="flex items-center gap-2 font-medium text-navy"><Check className="h-4 w-4 text-teal" />{b.t}</div>
                      <p className="mt-1.5 text-sm text-muted-foreground">{b.d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <Link to="/cognitive-behavioral-therapy" className="mt-8 inline-flex items-center gap-2 font-medium text-teal hover:gap-3 transition-all">
                  Explore our CBT approach <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ INTERACTIVE CBT CYCLE ============================ */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="The Cycle" title="How thoughts become patterns." 
            intro="Every experience runs through the same loop. CBT teaches you exactly where — and how — to interrupt it." />
          <div className="mt-16"><CBTCycle /></div>
        </div>
      </section>

      {/* ============================ HOW CBT WORKS (FLOW) ============================ */}
      <section className="relative overflow-hidden bg-navy py-24 lg:py-32">
        <div className="pointer-events-none absolute inset-0 grain opacity-[0.1]" />
        <div className="pointer-events-none absolute -top-20 left-1/4 h-80 w-80 rounded-full bg-teal/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal><span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-teal-light">How CBT Works</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight text-white text-balance">Change one link, and the whole chain shifts.</h2></Reveal>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_CBT.map((h, i) => (
              <motion.div key={h.label} variants={item} className="relative">
                <div className="rounded-3xl glass-navy p-8 text-center">
                  <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-teal/15 text-teal-light">
                    <Icon name={h.icon} className="h-8 w-8" />
                  </span>
                  <div className="mt-5 font-display text-2xl text-white">{h.label}</div>
                </div>
                {i < HOW_CBT.length - 1 && (
                  <div className="absolute right-[-18px] top-1/2 hidden -translate-y-1/2 lg:block">
                    <ArrowUpRight className="h-6 w-6 rotate-45 text-teal-light/50" />
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============================ WE HELP YOU ============================ */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading eyebrow="Outcomes, not labels" title="We help you…" 
            intro="Therapy here is about how you want to feel and live — not just a diagnosis on a form." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-12 flex flex-wrap gap-3">
            {WE_HELP.map((w) => (
              <motion.span key={w} variants={item}
                className="group cursor-default rounded-full border border-navy-100 bg-white px-6 py-3 text-navy shadow-soft transition-all hover:border-teal/40 hover:bg-teal hover:text-white hover:-translate-y-0.5"
                data-testid="we-help-chip">
                {w}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============================ CONDITIONS ============================ */}
      <section className="bg-secondary py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Conditions We Treat" title="Specialized, evidence-based care." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {CONDITIONS.map((c) => (
              <motion.div key={c.name} variants={item}>
                <Link to={c.to}
                  className="group flex h-full items-center gap-4 rounded-2xl border border-navy-100 bg-white p-5 shadow-soft transition-all hover:-translate-y-1 hover:shadow-premium hover:border-teal/30"
                  data-testid="condition-card">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-softblue/60 text-teal transition-colors group-hover:bg-teal group-hover:text-white">
                    <Icon name={c.icon} className="h-6 w-6" />
                  </span>
                  <span className="font-medium text-navy leading-tight">{c.name}</span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============================ TREATMENT PROCESS TIMELINE ============================ */}
      <ProcessTimeline />

      {/* ============================ MEASUREMENT-BASED CARE ============================ */}
      <section className="bg-secondary py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <div>
              <SectionHeading eyebrow="Measurement-Based Care" title="Because progress should be measurable."
                intro="At Beacon, we regularly track your improvement using validated, evidence-based assessment tools. That means therapy stays focused, effective, and personalised — and you can actually see how far you've come." />
              <div className="mt-8 space-y-4">
                {["Validated symptom questionnaires at key checkpoints", "Clear, visual progress you can see on paper", "Data-informed adjustments to keep therapy effective"].map((t, i) => (
                  <Reveal key={t} delay={i * 0.06}>
                    <div className="flex items-start gap-3">
                      <span className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-teal/10 text-teal"><Check className="h-3.5 w-3.5" /></span>
                      <span className="text-navy/80">{t}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.25}>
                <div className="mt-8">
                  <div className="text-xs font-semibold uppercase tracking-wider text-teal">Validated assessment tools we use</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {["OQ-45", "GAD-7", "PHQ-9", "CDI-2", "SCARED", "C-SSRS", "AUDIT / AUDIT-C", "MDQ"].map((tool) => (
                      <span key={tool} className="rounded-full border border-navy-100 bg-white px-3 py-1.5 text-sm text-navy/75 shadow-soft">{tool}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-navy-100 bg-white p-8 shadow-premium">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-navy">Progress snapshot</span>
                  <span className="rounded-full bg-teal/10 px-3 py-1 text-xs font-medium text-teal">Improving</span>
                </div>
                <div className="mt-8 space-y-5">
                  {[
                    { l: "Emotional Wellbeing", s: 82 },
                    { l: "Daily Functioning", s: 76 },
                    { l: "Sleep Quality", s: 68 },
                    { l: "Stress Management", s: 74 },
                    { l: "Goal Completion", s: 88 },
                  ].map((b, i) => (
                    <div key={b.l}>
                      <div className="mb-2 flex justify-between text-sm"><span className="text-navy/70">{b.l}</span><span className="font-medium text-teal">{b.s}%</span></div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-navy-50">
                        <motion.div initial={{ width: 0 }} whileInView={{ width: `${b.s}%` }} viewport={{ once: true }}
                          transition={{ duration: 1.1, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                          className="h-full rounded-full bg-gradient-to-r from-teal to-teal-light" />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-8 text-xs text-muted-foreground">Illustrative example for demonstration only — not real patient data. Every treatment plan is personalised.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================ FOUNDER SPOTLIGHT ============================ */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div className="relative mx-auto max-w-md">
                <div className="pointer-events-none absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-softblue to-transparent blur-2xl opacity-80" />
                <img src={FOUNDER.photo} alt={`${FOUNDER.name}, ${FOUNDER.credentials}`}
                  className="relative w-full rounded-[2rem] object-cover shadow-premium aspect-[4/5]" />
                <div className="absolute -bottom-6 -right-2 sm:right-6 rounded-2xl glass shadow-premium px-6 py-4">
                  <div className="font-display text-3xl text-navy">2017</div>
                  <div className="text-xs text-muted-foreground max-w-[9rem]">In private practice, providing CBT</div>
                </div>
              </div>
            </Reveal>
            <div>
              <SectionHeading eyebrow="Meet the Founder"
                title="Care from a counselor who's walked hard roads."
                intro="Beacon Counseling was founded by Dragan David Pecirep, MS, LPC — a Licensed Professional Counselor and U.S. Army combat veteran who brings evidence-based CBT together with the practical wisdom of Stoic philosophy." />
              <div className="mt-7 flex flex-wrap gap-2">
                {FOUNDER.specialties.map((s) => (
                  <span key={s} className="rounded-full bg-softblue/50 px-4 py-1.5 text-sm text-navy/75">{s}</span>
                ))}
              </div>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/therapists">
                    <Button className="rounded-full bg-navy hover:bg-teal text-white h-12 px-6 transition-colors" data-testid="meet-team-btn">
                      Read David's Story <ArrowUpRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                  <Button variant="outline" onClick={openDialog}
                    className="rounded-full border-navy/20 h-12 px-6 hover:bg-secondary">
                    Book with David
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ TESTIMONIALS ============================ */}
      <section className="bg-navy py-24 lg:py-32 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal><span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-teal-light">Client Stories</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight text-white">Real change, in their words.</h2></Reveal>
          </div>
          <Reveal delay={0.1}>
            <Carousel opts={{ loop: true, align: "start" }} className="mt-14">
              <CarouselContent>
                {TESTIMONIALS.map((t, i) => (
                  <CarouselItem key={i} className="md:basis-1/2 lg:basis-1/3">
                    <div className="h-full rounded-3xl glass-navy p-8" data-testid="testimonial-card">
                      <Quote className="h-8 w-8 text-teal-light/60" />
                      <p className="mt-5 text-lg leading-relaxed text-white/85">"{t.quote}"</p>
                      <div className="mt-6 flex items-center gap-1 text-teal-light">
                        {[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-current" />)}
                      </div>
                      <div className="mt-4 text-sm"><span className="font-medium text-white">{t.name}</span><span className="text-white/50"> · {t.detail}</span></div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="mt-8 flex justify-center gap-3">
                <CarouselPrevious className="static translate-y-0 border-white/20 bg-white/5 text-white hover:bg-white/15 hover:text-white" />
                <CarouselNext className="static translate-y-0 border-white/20 bg-white/5 text-white hover:bg-white/15 hover:text-white" />
              </div>
            </Carousel>
          </Reveal>
        </div>
      </section>

      {/* ============================ INSURANCE ============================ */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Insurance" title="Most major insurance accepted."
            intro="We believe great care should be accessible. We accept most major plans and will help verify your benefits before your first visit." />
          <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-4">
            {INSURANCE.map((n, i) => (
              <Reveal key={n} delay={i * 0.04}>
                <div className="flex min-h-[5.5rem] items-center justify-center rounded-2xl border border-navy-100 bg-white px-4 py-3 text-center text-sm font-medium leading-snug text-navy/70 shadow-soft transition-colors hover:text-teal">
                  {n}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-8 text-center">
              <Link to="/insurance-and-fees" className="inline-flex items-center gap-2 font-medium text-teal hover:gap-3 transition-all">
                See insurance & fees details <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================ FAQ ============================ */}
      <section className="bg-secondary py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-12">
          <SectionHeading center eyebrow="FAQ" title="Questions, answered." />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-navy-100">
                  <AccordionTrigger className="text-left font-display text-lg text-navy hover:text-teal hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}

function ProcessTimeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section ref={ref} className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <SectionHeading center eyebrow="Our Treatment Process" title="A clear path from stuck to thriving."
          intro="Therapy at Beacon follows a proven, collaborative process — so you always know where you are and where you're headed." />
        <div className="relative mx-auto mt-16 max-w-3xl">
          <div className="absolute left-[27px] top-2 bottom-2 w-[2px] bg-navy-100 md:left-1/2 md:-translate-x-1/2">
            <motion.div style={{ height: lineHeight }} className="w-full bg-gradient-to-b from-teal to-teal-light" />
          </div>
          <div className="space-y-8">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={0.03}>
                <div className={`relative flex items-start gap-6 md:gap-0 ${i % 2 ? "md:flex-row-reverse" : ""}`}>
                  <div className="z-10 grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-navy font-display text-lg text-white shadow-premium md:absolute md:left-1/2 md:-translate-x-1/2">
                    {p.step}
                  </div>
                  <div className={`flex-1 md:w-1/2 ${i % 2 ? "md:pr-16 md:text-right" : "md:pl-16"}`}>
                    <div className="rounded-2xl border border-navy-100 bg-white p-6 shadow-soft">
                      <h3 className="font-display text-xl text-navy">{p.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.text}</p>
                    </div>
                  </div>
                  <div className="hidden md:block md:w-1/2" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
